/**
 * The OUTBOX: what this player has done that the server has not confirmed yet.
 *
 * Both gestures a player makes most often - saying something, reacting to something - used to be
 * one-stage: send, then wait for the BROADCAST to come back before anything appeared on screen. The
 * round trip is server write → ack → broadcast → fold → render, and on a slow topology that is a
 * visible pause after every single line. Worse, a refused send left nothing behind at all: the text
 * you typed was gone and you had to remember it to try again.
 *
 * So there are two stages now. Stage one is here: the gesture is recorded the instant it is made and
 * rendered immediately, marked as in flight. Stage two is the committed event arriving through the
 * store exactly as it always did - at which point the entry here stops being drawn.
 *
 * ## Why this is NOT in the store
 *
 * The store is the fold of COMMITTED events and nothing else, and three things depend on that:
 *
 * 1. The persistence worker caches sessions to IndexedDB for warm start. An optimistic line written
 *    into `view.chat` would survive a reload as though it had been sent - a message that never
 *    happened, resurrected as history. That is the sharpest hazard here and it is why this file
 *    exists rather than four lines inside the store.
 * 2. `stateHash` comparison drives resync. Uncommitted chat in the fold would read as drift.
 * 3. Replay. The store's contents are supposed to be reproducible from the log.
 *
 * Nothing in here is authoritative, nothing in here is persisted, and everything in here is
 * eventually replaced by the real thing.
 *
 * ## How an entry stops being drawn
 *
 * `AckPayload` already carries `seq` - "first committed seq, present only when accepted" - so no wire
 * change was needed to correlate a pending entry with its committed self. The rule handles both
 * arrival orders with no flicker and no duplicate:
 *
 * > A sending entry is drawn until it has an acked seq AND the store holds that seq.
 *
 * Ack first → it lingers until the broadcast folds. Broadcast first → it disappears the moment the
 * ack names the seq. There is no window in which the same line is on screen twice.
 */

/** Where an unconfirmed gesture has got to. */
export type OutboxState = 'sending' | 'failed';

/** A line this player said that the server has not broadcast back yet. */
export interface PendingLine {
  /** The command id it was sent under - the identity a retry moves off, and the render key. */
  cmdId: string;
  session: string;
  /** The room it was typed in; null means the default room, exactly as the store means it. */
  channel: string | null;
  text: string;
  state: OutboxState;
  /** The committed seq the ack named, or null until it is accepted. */
  seq: number | null;
  /** Why it was refused, in the words the server used. Null while it is still in flight. */
  error: string | null;
}

/** A reaction this player made that has not been committed back yet. */
export interface PendingReaction {
  cmdId: string;
  session: string;
  /** The seq of the message being reacted TO - known at send time, so this needs no correlation. */
  target: number;
  reaction: string;
  state: OutboxState;
  error: string | null;
}

/**
 * The unconfirmed half of what this player has done. Pure and DOM-free: it is handed the question
 * "does the store hold this seq yet?" rather than reaching for the store itself, which is what keeps
 * it unit-testable and keeps the dependency pointing one way.
 */
export class Outbox {
  private lines: PendingLine[] = [];
  private reactions: PendingReaction[] = [];

  /** Records a line as sent-but-unconfirmed. Returns the entry so a caller can settle it later. */
  say(cmdId: string, session: string, channel: string | null, text: string): PendingLine {
    const entry: PendingLine = {
      cmdId,
      session,
      channel,
      text,
      state: 'sending',
      seq: null,
      error: null,
    };
    this.lines.push(entry);
    return entry;
  }

  /** Records a reaction as made-but-unconfirmed. */
  react(cmdId: string, session: string, target: number, reaction: string): PendingReaction {
    const entry: PendingReaction = {
      cmdId,
      session,
      target,
      reaction,
      state: 'sending',
      error: null,
    };
    this.reactions.push(entry);
    return entry;
  }

  /**
   * The server accepted it and named the seq it committed at. The entry stays until the broadcast for
   * that seq folds - see the class comment for why both orders are safe.
   */
  accepted(cmdId: string, seq: number | null): void {
    const line = this.lines.find((l) => l.cmdId === cmdId);
    if (line) {
      line.seq = seq;
      return;
    }

    // A reaction has no correlation problem: it is about a seq the client already knew, so acceptance
    // is simply the end of it. The committed event will fold into the store's own reaction map.
    this.reactions = this.reactions.filter((r) => r.cmdId !== cmdId);
  }

  /**
   * The server refused it, or it never got there.
   *
   * The entry STAYS, marked failed and holding the text. A refusal that deletes what you typed makes
   * the player reconstruct their own sentence from memory, which is a worse failure than the one that
   * caused it (standards/messages.md: the useful half of a problem message is the next move, and here
   * the next move needs the words back).
   */
  refused(cmdId: string, error: string): void {
    const line = this.lines.find((l) => l.cmdId === cmdId);
    if (line) {
      line.state = 'failed';
      line.error = error;
    }

    const reaction = this.reactions.find((r) => r.cmdId === cmdId);
    if (reaction) {
      reaction.state = 'failed';
      reaction.error = error;
    }
  }

  /** Drops an entry entirely - what a retry does to the attempt it replaces, and what a dismiss does. */
  forget(cmdId: string): void {
    this.lines = this.lines.filter((l) => l.cmdId !== cmdId);
    this.reactions = this.reactions.filter((r) => r.cmdId !== cmdId);
  }

  /** The failed line under this id, for a retry that needs to resend its text. */
  lineAt(cmdId: string): PendingLine | null {
    return this.lines.find((l) => l.cmdId === cmdId) ?? null;
  }

  /**
   * The lines to draw in one room, in the order they were said.
   *
   * `committed` answers "has the store folded this seq yet?". An accepted line whose broadcast has
   * landed is not returned - the store is already drawing the real one, and drawing both would be the
   * duplicate this whole scheme exists to avoid.
   */
  linesIn(
    session: string,
    channel: string | null,
    committed: (seq: number) => boolean,
  ): PendingLine[] {
    return this.lines.filter(
      (l) =>
        l.session === session &&
        l.channel === channel &&
        (l.state === 'failed' || l.seq === null || !committed(l.seq)),
    );
  }

  /**
   * The reactions to draw on one message. An accepted reaction is dropped on the ack, so what is left
   * here is either still in flight or refused - and a refused one is still worth drawing, because a
   * reaction that silently did not happen teaches the player their tap does nothing.
   */
  reactionsOn(
    session: string,
    target: number,
    committed: (reaction: string) => boolean,
  ): PendingReaction[] {
    return this.reactions.filter(
      (r) =>
        r.session === session &&
        r.target === target &&
        (r.state === 'failed' || !committed(r.reaction)),
    );
  }

  /** Everything unconfirmed in a session - for a client that wants to say "3 not sent". */
  pendingIn(session: string): { lines: PendingLine[]; reactions: PendingReaction[] } {
    return {
      lines: this.lines.filter((l) => l.session === session),
      reactions: this.reactions.filter((r) => r.session === session),
    };
  }
}
